import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import {
    normalizeErrorCode,
    PROMOTION_ERROR_CODE,
    PROMOTION_ERROR_CODES,
} from "@/shared/gift-cards.constants";
import { getGiftCardCheckoutToken } from "@/shared/helpers/gift-card-checkout-token";
import {
    GIFT_CARD_CLOSE_DESTINATIONS,
    postToApp,
    WEBVIEW_MESSAGE_TYPES,
} from "@/shared/webview-bridge.constants";

import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackPageLoad, trackUserEvent } from "@/analytics/tracking";

import { useCurrentLocale } from "@/context/LanguageContext";

import ActionButton from "@/components/buttons/action-button";
import { KINDS, SIZES } from "@/components/buttons/buttons.constants";

import { useInAppDarkMode } from "@/hooks/useInAppDarkMode";

import AlertCircleIconArcade from "../../assets/icons/alert-circle-arcade.svg";
import styles from "./gift-card-promo-failed.module.scss";

type Props = {
    sku: string;
    isGift: boolean;
    // Which PROMOTION_* code the service returned. Tracked, not shown — the copy is
    // deliberately the same for both (one screen, one message).
    reason: PROMOTION_ERROR_CODE;
};

// `offerState` uses the same values as the app and checkout promo events.
const OFFER_STATES: Record<PROMOTION_ERROR_CODE, string> = {
    [PROMOTION_ERROR_CODES.UNAVAILABLE]: "UNAVAILABLE",
    [PROMOTION_ERROR_CODES.ALREADY_CLAIMED]: "ALREADY_CLAIMED",
};

/*
 * In-app terminal notice for the gift card checkout webview, shown when the order
 * status poll reports a `fulfil_error` carrying a PROMOTION_* error_code: the
 * promotional offer attached to the order could not be applied (the campaign ended /
 * exhausted its budget, or this customer already claimed their entitlement) so the
 * service cancelled the order.
 *
 * Unlike the generic "Sorry, something went wrong" overlay this is NOT a "try again"
 * situation — retrying with the same offer would fail the same way. Instead we say the
 * offer ended, reassure them they weren't charged, and make clear they can still buy
 * at the regular price.
 *
 * Layout follows the generic error overlay (red alert glyph + heading + body) with the
 * webview shell and CTA treatment of GiftCardOrderDelayed.
 *
 * CTA: "Back to gift card" posts onGiftCardClose with `{ destination: "giftCard" }`.
 * Without the payload the app would resolve the close to the gift card wall, because
 * the portal redirect leaves status=SUCCESS in the webview URL; with it, app builds
 * that include the matching handler goBack() to the order screen directly beneath
 * (its catalog re-pull on webview close then shows the regular price). Older builds
 * ignore the payload and land on the wall, where the card can be re-opened.
 */
export const GiftCardPromoFailed: React.FC<Props> = ({ sku, isGift, reason }) => {
    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const router = useRouter();
    const isDark = useInAppDarkMode();

    useEffect(() => {
        trackPageLoad(router.asPath, locale, EVENTS.GIFT_CARD_PROMO_FAILED_VIEW, {
            sku,
            checkoutToken: getGiftCardCheckoutToken(),
            offerState: OFFER_STATES[normalizeErrorCode(reason) as PROMOTION_ERROR_CODE],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const messageKey = isGift
        ? "giftCards.promoFailed.giftMessage"
        : "giftCards.promoFailed.message";

    const onBackToGiftCard = () => {
        trackUserEvent(
            locale,
            router.asPath,
            UI_CONTEXTS.GIFTCARD_ORDER_CONFIRMATION,
            EVENTS.GIFT_CARD_PROMO_FAILED_BACK_CLICK,
            { sku, reason }
        );
        postToApp(WEBVIEW_MESSAGE_TYPES.ON_GIFT_CARD_CLOSE, {
            destination: GIFT_CARD_CLOSE_DESTINATIONS.GIFT_CARD,
        });
    };

    return (
        <div className={classNames(styles.container, { [styles.dark]: isDark })}>
            <div className={styles.iconWrapper} aria-hidden="true">
                <AlertCircleIconArcade viewBox="0 0 64 64" />
            </div>
            <h2 className={styles.header}>{t("giftCards.promoFailed.header")}</h2>
            <p className={styles.message}>{t(messageKey)}</p>
            <div className={styles.ctaContainer}>
                <div className={styles.backButton}>
                    <ActionButton
                        disabled={false}
                        fluid={true}
                        forceArcade={true}
                        kind={KINDS.BLACK}
                        label={t("giftCards.promoFailed.backToGiftCard")}
                        onClick={onBackToGiftCard}
                        size={SIZES.LARGE}
                    />
                </div>
            </div>
        </div>
    );
};
